import { useState, useEffect } from 'react';
import { Dialog, DialogActionsBar } from '@progress/kendo-react-dialogs';
import { Button } from '@progress/kendo-react-buttons';
import { RadioGroup } from '@progress/kendo-react-inputs';
import { Slider } from '@progress/kendo-react-inputs';
import { TextArea } from '@progress/kendo-react-inputs';
import { ProgressBar } from '@progress/kendo-react-progressbars';
import { Stepper } from '@progress/kendo-react-layout';
import { useSchedule } from '../context/ScheduleContext';
import { CrisisPanel } from './CrisisPanel';
import type { ScreenerCompletePayload } from './screenerTypes';

// ─── Stepper label helper ──────────────────────────────────────────────────

function stepperItems(count: number, current: number) {
  return Array.from({ length: count }, (_, i) => ({
    label: `Q${i + 1}`,
    disabled: i > current,
  }));
}

// ─── ScreenerGateDialog ────────────────────────────────────────────────────

interface ScreenerGateDialogProps {
  onComplete: (responses: { questionId: string; value: number | string }[]) => ScreenerCompletePayload | null;
  onClose: () => void;
  onCrisisAcknowledged: () => void;
}

function ScreenerGateDialog({ onComplete, onClose, onCrisisAcknowledged }: ScreenerGateDialogProps) {
  const { screenerState, screenerActiveScreener, screenerCrisisData, emit } = useSchedule();
  const [currentStep, setCurrentStep] = useState(0);
  const [responses, setResponses] = useState<Record<string, number | string>>({});

  // Reset internal state whenever a new screener opens.
  useEffect(() => {
    if (screenerState === 'SCREENER_OPEN') {
      setCurrentStep(0);
      setResponses({});
    }
  }, [screenerState]);

  if (screenerState === 'IDLE' || screenerState === 'AWAITING_CONFIG') return null;

  const isCrisis = screenerState === 'CRISIS_PANEL';

  const questions = screenerActiveScreener?.questions ?? [];
  const isLast = currentStep === questions.length - 1;
  const question = questions[currentStep];
  const progressPct = questions.length > 0 ? ((currentStep + 1) / questions.length) * 100 : 0;

  const handleNext = () => {
    if (isLast) {
      const responseArray = questions.map((q) => ({
        questionId: q.id,
        value: responses[q.id] ?? (q.type === 'text' ? '' : 0),
      }));
      const payload = onComplete(responseArray);
      // In postMessage mode (payload returned) emit it; direct-React mode handles internally.
      if (payload) {
        emit({ type: 'SCREENER_COMPLETE', payload });
      }
    } else {
      setCurrentStep((s) => s + 1);
    }
  };

  const handleBack = () => setCurrentStep((s) => s - 1);

  const setResponse = (questionId: string, value: number | string) =>
    setResponses((prev) => ({ ...prev, [questionId]: value }));

  return (
    <Dialog
      title={isCrisis ? 'Crisis Support Resources' : (screenerActiveScreener?.title ?? 'Screener')}
      onClose={isCrisis ? undefined : onClose}
      closeIcon={!isCrisis}
      width={560}
      style={{ userSelect: 'none' }}
    >
      {isCrisis && screenerCrisisData ? (
        <CrisisPanel
          message={screenerCrisisData.message}
          resources={screenerCrisisData.resources}
          onAcknowledge={onCrisisAcknowledged}
        />
      ) : screenerActiveScreener ? (
        <>
          {/* Progress bar — step X of N */}
          <div style={{ marginBottom: 12 }}>
            <ProgressBar value={progressPct} />
            <div style={{ fontSize: 11, color: '#64748b', textAlign: 'right', marginTop: 3 }}>
              Step {currentStep + 1} of {questions.length}
            </div>
          </div>

          {/* Step track */}
          {questions.length > 1 && (
            <div style={{ marginBottom: 16 }}>
              <Stepper
                value={currentStep}
                items={stepperItems(questions.length, currentStep)}
                linear
              />
            </div>
          )}

          {/* Instructions — shown on first step only */}
          {currentStep === 0 && screenerActiveScreener.instructions && (
            <p style={{ fontSize: 13, color: '#475569', marginBottom: 16, lineHeight: 1.5 }}>
              {screenerActiveScreener.instructions}
            </p>
          )}

          {/* Question */}
          {question && (
            <div style={{ minHeight: 140, paddingBottom: 8 }}>
              <p style={{ fontWeight: 600, fontSize: 14, marginBottom: 14, lineHeight: 1.4 }}>
                {question.text}
              </p>

              {question.type === 'likert' && question.options && (
                <RadioGroup
                  data={question.options.map((o) => ({ label: o.label, value: o.value }))}
                  value={responses[question.id] ?? null}
                  onChange={(e) => setResponse(question.id, e.value as number)}
                  layout="vertical"
                />
              )}

              {question.type === 'numeric' && (
                <div style={{ padding: '8px 16px' }}>
                  <Slider
                    value={(responses[question.id] as number) ?? (question.min ?? 0)}
                    min={question.min ?? 0}
                    max={question.max ?? 10}
                    step={1}
                    onChange={(e) => setResponse(question.id, e.value)}
                    style={{ width: '100%' }}
                  />
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: '#64748b', marginTop: 4 }}>
                    <span>{question.min ?? 0}</span>
                    <span style={{ fontWeight: 600, fontSize: 14, color: '#1e293b' }}>
                      {(responses[question.id] as number) ?? (question.min ?? 0)}
                    </span>
                    <span>{question.max ?? 10}</span>
                  </div>
                </div>
              )}

              {question.type === 'text' && (
                <TextArea
                  value={(responses[question.id] as string) ?? ''}
                  onChange={(e) => setResponse(question.id, e.value ?? '')}
                  rows={4}
                  style={{ width: '100%' }}
                  placeholder="Type your response here…"
                />
              )}
            </div>
          )}

          <DialogActionsBar>
            <Button onClick={handleBack} disabled={currentStep === 0}>
              Back
            </Button>
            <Button themeColor="primary" onClick={handleNext}>
              {isLast ? 'Submit' : 'Next'}
            </Button>
          </DialogActionsBar>
        </>
      ) : null}
    </Dialog>
  );
}

// ─── ScreenerGateDialogMount ───────────────────────────────────────────────
// Placed inside ScheduleContext.Provider so it has full context access.
// Rendered unconditionally; guards are inside ScreenerGateDialog itself.

export function ScreenerGateDialogMount() {
  const {
    screenerGate,
    screenerState,
    screenerOnCompleted,
    screenerOnCrisisAcknowledged,
    screenerReset,
  } = useSchedule();

  if (!screenerGate?.enabled) return null;
  if (screenerState === 'IDLE' || screenerState === 'AWAITING_CONFIG') return null;

  return (
    <ScreenerGateDialog
      onComplete={screenerOnCompleted}
      onClose={screenerReset}
      onCrisisAcknowledged={screenerOnCrisisAcknowledged}
    />
  );
}
